export interface DeviceProfile {
  fps: number;
  cores: number;
  memory: number | null;
  reducedMotion: boolean;
  lowPerf: boolean;
}

const LOCAL_STORAGE_KEY = "device_profile";

const measureFPS = (durationMs = 1000): Promise<number> => {
  return new Promise((resolve) => {
    let frames = 0;
    const startTime = performance.now();
    let completed = false;

    const timeoutId = setTimeout(() => {
      if (!completed) {
        completed = true;
        resolve(30); // Default low if it timed out/suspended
      }
    }, durationMs + 500);

    const tick = () => {
      if (completed) return;
      frames++;
      const now = performance.now();
      if (now - startTime >= durationMs) {
        completed = true;
        clearTimeout(timeoutId);
        const actualDuration = now - startTime;
        const fps = (frames * 1000) / actualDuration;
        resolve(Math.round(fps));
      } else {
        requestAnimationFrame(tick);
      }
    };

    requestAnimationFrame(tick);
  });
};

export const getStoredProfile = (): DeviceProfile | null => {
  try {
    const raw = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (raw) {
      return JSON.parse(raw);
    }
  } catch {}
  return null;
};

export const applyDeviceProfile = (): void => {
  const profile = getStoredProfile();
  const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const isSlow = (profile && profile.lowPerf) || prefersReduced;

  if (isSlow) {
    document.documentElement.classList.add("low-perf");
    if (document.body) {
      document.body.classList.add("low-perf");
    }
  } else {
    document.documentElement.classList.remove("low-perf");
    if (document.body) {
      document.body.classList.remove("low-perf");
    }
  }
};

export const detectAndSaveDeviceProfile = async (): Promise<DeviceProfile> => {
  console.log("Detecting device profile, to see if we need to disable animations...");
  const existing = getStoredProfile();
  if (existing) {
    return existing;
  }

  const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const cores = navigator.hardwareConcurrency || 4;
  const memory = (navigator as any).deviceMemory || null;

  // Measure FPS over 1 second (1000ms)
  const fps = await measureFPS(1000);

  const lowPerf = prefersReduced || fps < 40 || cores < 4 || (memory !== null && memory < 4);

  const profile: DeviceProfile = {
    fps,
    cores,
    memory,
    reducedMotion: prefersReduced,
    lowPerf,
  };

  try {
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(profile));
  } catch {}

  applyDeviceProfile();
  return profile;
};

// Listen for prefers-reduced-motion changes dynamically
try {
  window.matchMedia("(prefers-reduced-motion: reduce)").addEventListener("change", () => {
    applyDeviceProfile();
  });
} catch {}

// Automatically run on load
applyDeviceProfile();
